// The Email Briefing as a MODAL over home (Figma 40009470-209128, coordinator's 2026-09-02 fix).
//
// Home's "Emails need attention" row used to push /emails/workspace: the middle became the Email
// Workspace, the strip under it went away, and the user had left home to read a dashboard. The
// fix is an overlay — the Briefing Email Inbox drawn INSIDE a dialog with home still behind it,
// closed to land exactly where you were.
//
// Two things about the overlay are decided here, pure, so they can be checked without mounting:
//
//   1. WHICH entries it lists. The dashboard's counts (lib/briefings/email.js) minus "N email
//      groups": the groups are the sender clusters the WORKSPACE derives, a listing of its own, and
//      the modal shows the mailbox's numbers, not the system-made groups (the brief: "don't show
//      system-made email groups in this modal").
//   2. WHAT the store is asked to do — and, as importantly, what it is NOT. The overlay never enters
//      the email session (`emailSession/open`): the session is what hands home's middle to the
//      workspace (lib/emailSession.js `emailWorkspaceOwnsMiddle`) and hides the Follow-ups strip,
//      which is the very navigation this replaces. It fetches the counts and an entry's threads
//      only, both of which the session store answers without being "open".

import { emailDashboardRows, emailBriefingGridOrder, emailEntryLabel } from '@/lib/briefings/email'
import { TEXT } from '@/lib/emailSession'

/** The value home's email row carries instead of a `route`: "open the Briefing modal". */
export const EMAIL_BRIEFING_MODAL = 'email-briefing'

/** The dashboard entries the modal does NOT draw. */
export const HIDDEN_MODAL_ENTRIES = ['groups']

/** An entry the modal can open — the groups entry never is. */
export function modalEntryAllowed(entry) {
  const key = String(entry || '')
  return !!key && !HIDDEN_MODAL_ENTRIES.includes(key)
}

/**
 * The stat rows the modal draws, in the frame's two-column reading order, each one a way into its
 * threads and marked when it is the one open. Same rows as the strip's dashboard, same rule (a
 * zero is not drawn), minus the hidden entries.
 */
export function modalStatRows(counts, openEntry = null) {
  const rows = emailDashboardRows(counts).filter(r => !HIDDEN_MODAL_ENTRIES.includes(r.key))
  return emailBriefingGridOrder(rows).map(r => ({ ...r, active: !!openEntry && r.key === openEntry }))
}

/** The dialog's heading: the inbox's name, or the open entry's own words ("9 Need action", busy-my-web#614). */
export function modalTitle(entry, counts) {
  if (!modalEntryAllowed(entry)) return TEXT.briefingInbox
  return emailEntryLabel(entry, counts) || TEXT.briefingInbox
}

/**
 * Which entry a stat click leaves open: the same row again closes it (the strip's own toggle rule),
 * a hidden entry is never opened.
 */
export function nextModalEntry(current, clicked) {
  if (!modalEntryAllowed(clicked)) return null
  return current === clicked ? null : clicked
}

/**
 * What the modal asks the store for when it is shown: the counts (a refresh — home fetched them
 * once, and the modal is the moment they are read) and a clean entry slate.
 */
export function openBriefingModal(store) {
  if (!store?.dispatch) return
  store.dispatch('emailSession/loadBriefing')
  store.dispatch('emailSession/openEntry', null)
}

/**
 * A stat clicked: the entry's threads are fetched (or, clicked again, dropped). Returns the entry
 * now open so the caller can record it — the caller owns which entry is open, not the Briefing
 * store's `emailCategory`, which belongs to the strip and would otherwise tint a strip that is not
 * showing the mailbox.
 */
export function openModalEntry(store, current, clicked) {
  const next = nextModalEntry(current, clicked)
  store?.dispatch?.('emailSession/openEntry', next)
  return next
}

/** Closing: nothing of the modal's is left in the store — the entry threads go with it. */
export function closeBriefingModal(store) {
  store?.dispatch?.('emailSession/openEntry', null)
}
